import { DropType } from '#lib/features/checkout/constants/drop-types.js';
import { FilterCollection } from '$global/features/filter-drawer/constants';
import type {
	FilterCheckboxList,
	FilterDrawerCustomAttributes,
	FilterSection,
	FilterSectionFilterType,
	FilterSections,
	FilterToggle,
} from '$global/features/filter-drawer/types';

enum Material {
	Print = 'Print',
	Sculpture = 'Sculpture',
	Tapestry = 'Tapestry',
	StainedGlass = 'Stained Glass',
}

enum FilterSectionId {
	Artists = '1',
	Materials = '2',
	EditionType = '3',
}

enum FilterTypeId {
	Artists = '1',
	Materials = '2',
	TimedRelease = '3',
	ApplicationOnly = '4',
	Charity = '5',
}

// The artist id is the checkbox value, which the filter drawer round-trips
// through the URL as a string. REST returns it as a number, GraphQL as a
// string, so it is stringified here for both.
export interface EditionsFilterArtist {
	id: string | number;
	name?: string | null;
}

const getArtists = (artists: EditionsFilterArtist[] = []): FilterSection => {
	const item: FilterCheckboxList = {
		__typename: 'Filter_Checkbox_List',
		id: '1',
		field_name: 'artists.Artists_id.id',
		label: '',
		select_all: true,
		type: 'checkbox',
		filter_checkboxes: artists?.map((artist, index) => {
			return {
				__typename: 'Filter_Checkbox',
				id: index.toString(),
				label: artist.name ?? null,
				tooltip_description: null,
				icon_name: null,
				default_state: false,
				value: String(artist.id),
			};
		}),
	};

	const filterType: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Checkbox_List,
		id: FilterTypeId.Artists,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.Artists,
		title: 'Artists',
		default_open: true,
		show_content_only: false,
		filter_type: [filterType],
	};
};

const getMaterials = (): FilterSection => {
	const materials = Object.values(Material);

	const item: FilterCheckboxList = {
		__typename: 'Filter_Checkbox_List',
		id: '1',
		field_name: 'edition_type',
		label: '',
		select_all: false,
		type: 'checkbox',
		filter_checkboxes: materials.map((material, index) => {
			return {
				__typename: 'Filter_Checkbox',
				id: index.toString(),
				label: material,
				tooltip_description: null,
				icon_name: null,
				default_state: false,
				value: material,
			};
		}),
	};

	const filterType: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Checkbox_List,
		id: FilterTypeId.Materials,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.Materials,
		title: 'Material',
		default_open: true,
		show_content_only: false,
		filter_type: [filterType],
	};
};

const getEditionType = (): FilterSection => {
	const item1: FilterToggle = {
		__typename: 'Filter_Toggle',
		id: '1',
		label: 'Timed Release',
		description: 'Filter by editions that had a timed release',
		tooltip_description: null,
		icon_name: null,
		field_name: 'timed_release',
		default_state: false,
	};

	const item2: FilterToggle = {
		__typename: 'Filter_Toggle',
		id: '2',
		label: 'Application Only',
		description: 'Filter by editions that were application based',
		tooltip_description: null,
		icon_name: null,
		field_name: 'application_only',
		default_state: false,
	};

	const item3: FilterToggle = {
		__typename: 'Filter_Toggle',
		id: '3',
		label: 'Charity',
		description: 'Filter by editions that support charity',
		tooltip_description: null,
		icon_name: null,
		field_name: 'charity',
		default_state: false,
	};

	const filterType1: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Toggle,
		id: FilterTypeId.TimedRelease,
		item: item1,
	};

	const filterType2: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Toggle,
		id: FilterTypeId.ApplicationOnly,
		item: item2,
	};

	const filterType3: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Toggle,
		id: FilterTypeId.Charity,
		item: item3,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.EditionType,
		title: 'Edition Type',
		default_open: true,
		show_content_only: false,
		filter_type: [filterType1, filterType2, filterType3],
	};
};

// Application Only also matches editions whose product is sold by application
// on its drop (`drop_config_type`), e.g. the application print of a hybrid
// drop. Off applies no filter, as before: the drawer drops an off toggle from
// the URL.
export const editionsGalleryFilterCustomAttributes: FilterDrawerCustomAttributes =
	{
		[FilterTypeId.ApplicationOnly]: ({ value }) =>
			value === true
				? {
						_or: [
							{ application_only: { _eq: true } },
							{ product: { drop_config_type: { _eq: DropType.Application } } },
						],
					}
				: null,
	};

export const getEditionsGalleryFilterSections = ({
	filterOptions,
}: {
	filterOptions: { Artists?: EditionsFilterArtist[] | null } | undefined;
}) => {
	const filterSections: FilterSections = [];

	if (filterOptions?.Artists) {
		filterSections.push(getArtists(filterOptions?.Artists));
	}

	filterSections.push(getMaterials());

	filterSections.push(getEditionType());

	return filterSections;
};
